import { Project } from "@/interfaces/dashboard";
import Link from "next/link";
import { useState, useRef, useEffect } from "react";
import { Edit3, Trash2, Share2, MoreVertical } from "lucide-react";
import { useRouter } from "next/navigation";

interface ProjectRowProps {
  project: Project;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  onShare: (id: string) => void;
  isLast: boolean;
}

const ProjectRow: React.FC<ProjectRowProps> = ({
  project,
  onEdit,
  onDelete,
  onShare,
  isLast,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const Router = useRouter();

  const formatDate = (dateString: string) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    const diffMonths = Math.floor(diffMs / 2592000000);

    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24)
      return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
    if (diffDays < 30) return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
    if (diffMonths < 12)
      return `${diffMonths} month${diffMonths > 1 ? "s" : ""} ago`;
    return date.toLocaleDateString();
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen]);

  return (
    <tr
      className={`hover:bg-[var(--hoverBackground)] transition-colors cursor-pointer ${
        !isLast ? "border-b border-primaryBorder" : ""
      }`}
      onClick={() => Router.push(`/workspace/${project._id}`)}
    >
      
      <td className="px-4 py-4 text-center">
        
        <Link
          href={`/workspace/${project._id}`}
          className="font-medium text-left text-[var(--primaryText)] hover:underline"
        >
          {project.name}
        </Link>
        {project.description && (
          <div className="text-xs mt-1 text-center text-[var(--primaryText)]">
            {project.description}
          </div>
        )}
      </td>
      <td className="px-4 py-4 text-center text-sm text-[var(--primaryText)]">
        {formatDate(project.createdAt)}
      </td>
      <td className="px-4 py-4 text-center text-sm text-[var(--primaryText)]">
        {formatDate(project.updatedAt)}
      </td>
      <td className="px-4 py-4 text-center text-sm text-[var(--primaryText)]">
        0
      </td>
      <td className="px-4 py-4 text-right relative">
        
        <div ref={menuRef} className="flex justify-center relative">
          {/* 3 Dots Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen((prev) => !prev);
            }}
            className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-[var(--foreground)] text-[var(--primaryText)] hover:text-[var(--background)] transition-colors"
            aria-label={`More options for ${project.name}`}
          >
            <MoreVertical className="w-5 h-5 " />
          </button>
          {/* Dropdown Menu - NOW A SIBLING ELEMENT */}
          {menuOpen && (
            <div className="absolute right-0 mt-[2rem] w-44 bg-[var(--background)] border border-primaryBorder rounded-md shadow-lg z-[9999]">
              
              <ul className="py-1 text-sm text-[var(--primaryText)]">
                
                <li>
                  
                  <button
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent row click
                      onEdit(project._id);
                      setMenuOpen(false);
                    }}
                    className="w-full px-4 py-2 flex items-center gap-2 hover:bg-[var(--hoverBackground)] transition-colors text-left"
                  >
                    <Edit3 className="w-4 h-4" /> Edit
                  </button>
                </li>
                <li>
                  
                  <button
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent row click
                      onDelete(project._id);
                      setMenuOpen(false);
                    }}
                    className="w-full px-4 py-2 flex items-center gap-2 hover:bg-[var(--hoverBackground)] transition-colors text-red-500 text-left"
                  >
                    <Trash2 className="w-4 h-4" /> Delete
                  </button>
                </li>
                <li>
                  
                  <button
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent row click
                      onShare(project._id);
                      setMenuOpen(false);
                    }}
                    className="w-full px-4 py-2 flex items-center gap-2 hover:bg-[var(--hoverBackground)] transition-colors text-left"
                  >
                    <Share2 className="w-4 h-4" /> Share
                  </button>
                </li>
              </ul>
            </div>
          )}
        </div>
      </td>
    </tr>
  );
};

export default ProjectRow;
